import React, { createContext, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Workflow } from '../types/workflow';

interface Tab {
  key: string;
  title: string;
  workflow: Workflow | null;
  hasUnsavedChanges: boolean;
}

interface WorkflowContextType {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  currentWorkflow: Workflow | null;
  setCurrentWorkflow: (workflow: Workflow | null) => void;
  checkUnsavedChanges: (callback: () => void) => void;
  discardChanges: () => void;
  tabs: Tab[];
  setTabs: (tabs: Tab[] | ((prevTabs: Tab[]) => Tab[])) => void;
  activeTabKey: string;
  setActiveTabKey: (key: string) => void;
  openWorkflowInNewTab: (workflow: Workflow | null) => void;
  updateTabWorkflow: (tabKey: string, workflow: Workflow | null) => void;
  switchToTab: (tabKey: string) => void;
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined);

export const WorkflowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState<Workflow | null>(null);
  const [tabs, setTabs] = useState<Tab[]>([
    { 
      key: uuidv4(),
      title: 'New Workflow',
      workflow: null,
      hasUnsavedChanges: false 
    }
  ]);
  const [activeTabKey, setActiveTabKey] = useState(tabs[0].key);

  const checkUnsavedChanges = (callback: () => void) => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        setHasUnsavedChanges(false);
        callback();
      }
    } else {
      callback();
    }
  };

  const discardChanges = () => {
    setHasUnsavedChanges(false);
    setCurrentWorkflow(null);
  };

  // Save current workflow state to active tab before switching
  const saveCurrentTabState = useCallback(() => {
    if (activeTabKey && currentWorkflow !== undefined) {
      setTabs(prevTabs =>
        prevTabs.map(tab =>
          tab.key === activeTabKey
            ? {
                ...tab,
                workflow: currentWorkflow,
                hasUnsavedChanges: hasUnsavedChanges
              }
            : tab
        )
      );
    }
  }, [activeTabKey, currentWorkflow, hasUnsavedChanges]);

  // Switch to a specific tab and load its state
  const switchToTab = useCallback((tabKey: string) => {
    // Save current tab state before switching
    saveCurrentTabState();
    
    // Find the target tab
    const targetTab = tabs.find(tab => tab.key === tabKey);
    if (targetTab) {
      setActiveTabKey(tabKey);
      setCurrentWorkflow(targetTab.workflow);
      setHasUnsavedChanges(targetTab.hasUnsavedChanges);
    }
  }, [tabs, saveCurrentTabState]);

  const updateTabWorkflow = useCallback((tabKey: string, workflow: Workflow | null) => {
    setTabs(prevTabs =>
      prevTabs.map(tab =>
        tab.key === tabKey
          ? {
              ...tab,
              workflow,
              title: workflow?.id || 'New Workflow',
              hasUnsavedChanges: true
            }
          : tab
      )
    );
    
    // If this is the active tab, update the current workflow state
    if (tabKey === activeTabKey) {
      setCurrentWorkflow(workflow);
      setHasUnsavedChanges(true);
    }
  }, [activeTabKey]);

  const openWorkflowInNewTab = useCallback((workflow: Workflow | null) => {
    // Save current tab state before creating new tab
    saveCurrentTabState();
    
    const newTabKey = uuidv4();
    
    // For new workflows or imported workflows without an ID, generate a new ID
    let newWorkflow = null;
    if (workflow) {
      // Deep clone the workflow but preserve the original ID for editing
      newWorkflow = {
        ...JSON.parse(JSON.stringify(workflow)),
        is_enabled: workflow.is_enabled ?? true
      };
    }

    const newTab: Tab = {
      key: newTabKey,
      title: newWorkflow?.id || 'New Workflow',
      workflow: newWorkflow,
      hasUnsavedChanges: false
    };

    setTabs(prevTabs => [...prevTabs, newTab]);
    setActiveTabKey(newTabKey);
    setCurrentWorkflow(newWorkflow);
    setHasUnsavedChanges(false);
  }, [saveCurrentTabState]);

  return (
    <WorkflowContext.Provider 
      value={{ 
        hasUnsavedChanges, 
        setHasUnsavedChanges,
        currentWorkflow,
        setCurrentWorkflow,
        checkUnsavedChanges,
        discardChanges,
        tabs,
        setTabs,
        activeTabKey,
        setActiveTabKey,
        openWorkflowInNewTab,
        updateTabWorkflow,
        switchToTab
      }}
    >
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};